/**
 * Press-and-hold control that opens the vault: a fingerprint in a ring that fills
 * while it's held (mouse, touch, or Space / Enter), drains if let go early, and
 * fires onComplete once full. The vault mirrors the charge (onCharge, 0..1) so
 * the door reacts while you hold.
 */
import { forwardRef, useEffect, useRef, useState } from 'react';

const HOLD_MS = 1500; // time to fill
const DRAIN = 2.5; // lets go this many times faster than it fills
const R = 44; // ring radius in the 100 x 100 viewBox
const C = 2 * Math.PI * R;

// forwardRef: on React 18 a function component doesn't receive `ref` as a prop.
export default forwardRef(function HoldToUnlock({ disabled = false, onCharge, onComplete }, ref) {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const holding = useRef(false);
  const p = useRef(0);
  const raf = useRef(0);
  const last = useRef(0);
  const cbs = useRef({ onCharge, onComplete });
  cbs.current = { onCharge, onComplete };

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const loop = (now) => {
    const dt = last.current ? now - last.current : 16;
    last.current = now;
    const step = dt / HOLD_MS;
    p.current = Math.min(1, Math.max(0, p.current + (holding.current ? step : -step * DRAIN)));
    setProgress(p.current);
    cbs.current.onCharge(p.current);
    if (p.current >= 1) {
      holding.current = false;
      setDone(true);
      navigator.vibrate?.(40);
      cbs.current.onComplete();
      return;
    }
    if (holding.current || p.current > 0) raf.current = requestAnimationFrame(loop);
    else last.current = 0;
  };

  const press = () => {
    if (disabled || done || holding.current) return;
    holding.current = true;
    cancelAnimationFrame(raf.current);
    last.current = 0;
    raf.current = requestAnimationFrame(loop);
  };
  const release = () => {
    holding.current = false;
  };

  return (
    <div
      ref={ref}
      className={`aops-hold${done ? ' is-done' : ''}`}
      style={{ opacity: disabled && !done ? 0 : undefined }}
    >
      <button
        type="button"
        className="aops-hold-btn"
        aria-label="Hold to unlock the vault"
        disabled={disabled || done}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          press();
        }}
        onPointerUp={release}
        onPointerCancel={release}
        onLostPointerCapture={release}
        onKeyDown={(e) => {
          if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            press();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === ' ' || e.key === 'Enter') release();
        }}
        onContextMenu={(e) => e.preventDefault()} // a long touch must not open the context menu
      >
        <svg className="aops-hold-ring" viewBox="0 0 100 100" aria-hidden="true">
          <circle className="aops-hold-track" cx="50" cy="50" r={R} />
          <circle
            className="aops-hold-fill"
            cx="50"
            cy="50"
            r={R}
            strokeDasharray={C}
            strokeDashoffset={C * (1 - progress)}
          />
        </svg>
        {/* fingerprint: the vault scans you while you hold */}
        <svg className="aops-hold-print" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6.8 5.2A8 8 0 0 1 19.6 11" />
          <path d="M4.6 8.2A8 8 0 0 0 4 11.6v1.6" />
          <path d="M7.4 18.6A12 12 0 0 1 7 15v-3.4a5 5 0 0 1 10 0v.8" />
          <path d="M17 15.6a16 16 0 0 1-.9 4.4" />
          <path d="M12 11.6V15a9 9 0 0 0 1.4 4.8" />
          <path d="M9.6 21a12 12 0 0 1-.4-1.2" />
          <path d="M19.8 14.2a20 20 0 0 1-.5 3.6" />
        </svg>
      </button>
      <span className="aops-hold-label" aria-hidden="true">
        {done ? 'Access granted' : progress > 0 ? 'Scanning' : 'Hold to unlock'}
      </span>
    </div>
  );
});
